import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';

const SchoolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isSchoolAdmin, isDistrict } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [schoolData, setSchoolData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const { notification, showError, showSuccess, hideNotification } = useNotification();
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    direction: 'desc',
  });

  useEffect(() => {
    fetchSchoolDetails();
  }, [id]);

  const fetchSchoolDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/schools/${id}/details`);
      setSchoolData(response.data.data);
      if (response.data.data.school) {
        setEditForm({
          name: response.data.data.school.name || '',
          district: response.data.data.school.district || '',
          address: response.data.data.school.address || '',
          contactEmail: response.data.data.school.contactEmail || '',
          contactPhone: response.data.data.school.contactPhone || '',
          licenseCount: response.data.data.school.licenseCount || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch school details:', error);
      showError('Failed to load school details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchool = async () => {
    try {
      const updateData = { ...editForm };
      if (isSchoolAdmin) {
        delete updateData.district;
        delete updateData.licenseCount;
      }
      await api.patch(`/admin/schools/${id}`, updateData);
      await fetchSchoolDetails();
      setEditMode(false);
      showSuccess('School updated successfully');
    } catch (error) {
      console.error('Failed to update school:', error);
      showError(error.response?.data?.message || 'Failed to update school');
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const sortUsers = (users) => {
    if (!users || users.length === 0) return users;
    
    const sorted = [...users];
    const { field, direction } = sortConfig;
    
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (field) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'enrollments':
          aValue = a.enrollments.length;
          bValue = b.enrollments.length;
          break;
        case 'createdAt':
        default:
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const handleAddLicenses = async () => {
    const additional = prompt('Enter number of licenses to add:');
    if (additional && !isNaN(parseInt(additional))) {
      try {
        await api.patch(`/admin/schools/${id}/licenses`, {
          additionalLicenses: parseInt(additional),
        });
        await fetchSchoolDetails();
        showSuccess('Licenses added successfully');
      } catch (error) {
        console.error('Failed to add licenses:', error);
        showError(error.response?.data?.message || 'Failed to add licenses');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading school details...</p>
        </div>
      </div>
    );
  }

  if (!schoolData || !schoolData.school) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">School not found</p>
        <button
          onClick={() => navigate('/schools')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark"
        >
          Back to Schools
        </button>
      </div>
    );
  }

  const { school, schoolAdmin, users, stats } = schoolData;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    ...(isDistrict ? [] : [{ id: 'stats', label: 'Statistics' }]),
    ...(isSchoolAdmin ? [] : [{ id: 'users', label: 'Users' }]),
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/schools')}
            className="text-primary hover:text-primaryDark mb-2 flex items-center"
          >
            ← Back to Schools
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{school.name}</h1>
          <p className="text-gray-600 mt-2">{school.district}</p>
        </div>
        {activeTab === 'overview' && (isAdmin || isSchoolAdmin) && (
          <button
            onClick={() => setEditMode(!editMode)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors"
          >
            {editMode ? 'Cancel Edit' : 'Edit School'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-md p-6">
        {activeTab === 'overview' && (
          <div>
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">School Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                    <input
                      type="text"
                      value={editForm.district}
                      onChange={(e) => setEditForm({ ...editForm, district: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                    <input
                      type="email"
                      value={editForm.contactEmail}
                      onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                    <input
                      type="tel"
                      value={editForm.contactPhone || ''}
                      onChange={(e) => setEditForm({ ...editForm, contactPhone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={editForm.address || ''}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">License Count</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.licenseCount}
                      onChange={(e) => setEditForm({ ...editForm, licenseCount: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}
                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveSchool}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      fetchSchoolDetails();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className={`grid grid-cols-2 ${isDistrict ? 'md:grid-cols-2' : 'md:grid-cols-4'} gap-4 mb-6`}>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Total Users</p>
                    <p className="text-xl font-bold text-gray-800">{stats.totalUsers}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Active Users</p>
                    <p className="text-xl font-bold text-gray-800">{stats.activeUsers}</p>
                  </div>
                  {!isDistrict && (
                    <>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Licenses Used</p>
                        <p className="text-xl font-bold text-gray-800">
                          {school.licensesUsed || 0} / {school.licenseCount}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">License Usage</p>
                        <p className="text-xl font-bold text-gray-800">
                          {school.licenseCount > 0 
                            ? Math.round(((school.licensesUsed || 0) / school.licenseCount) * 100) 
                            : 0}%
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-3">School Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">District</p>
                        <p className="font-medium text-gray-800">{school.district}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Contact Email</p>
                        <p className="font-medium text-gray-800">{school.contactEmail}</p>
                      </div>
                      {school.contactPhone && (
                        <div>
                          <p className="text-gray-600">Contact Phone</p>
                          <p className="font-medium text-gray-800">{school.contactPhone}</p>
                        </div>
                      )}
                      {school.address && (
                        <div>
                          <p className="text-gray-600">Address</p>
                          <p className="font-medium text-gray-800">{school.address}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {schoolAdmin && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-700 mb-3">School Administrator</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Name</p>
                          <p className="font-medium text-gray-800">
                            {schoolAdmin.firstName} {schoolAdmin.lastName}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Email</p>
                          <p className="font-medium text-gray-800">{schoolAdmin.email}</p>
                        </div>
                        {schoolAdmin.phoneNumber && (
                          <div>
                            <p className="text-gray-600">Phone</p>
                            <p className="font-medium text-gray-800">{schoolAdmin.phoneNumber}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-600">Status</p>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                            schoolAdmin.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {schoolAdmin.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {isAdmin && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold text-gray-700">License Management</h3>
                        <button
                          onClick={handleAddLicenses}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark text-sm"
                        >
                          + Add Licenses
                        </button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Licenses Used</span>
                          <span className="font-medium text-gray-800">
                            {school.licensesUsed || 0} / {school.licenseCount}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-primary h-3 rounded-full transition-all"
                            style={{
                              width: `${school.licenseCount > 0 
                                ? ((school.licensesUsed || 0) / school.licenseCount) * 100 
                                : 0}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-6">School Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">Total Users</p>
                <p className="text-4xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">Active Users</p>
                <p className="text-4xl font-bold text-gray-900">{stats.activeUsers}</p>
                {stats.totalUsers > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((stats.activeUsers / stats.totalUsers) * 100)}% of total
                  </p>
                )}
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">Pending Users</p>
                <p className="text-4xl font-bold text-gray-900">{stats.pendingUsers}</p>
                {stats.totalUsers > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((stats.pendingUsers / stats.totalUsers) * 100)}% of total
                  </p>
                )}
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">Total Enrollments</p>
                <p className="text-4xl font-bold text-gray-900">{stats.totalEnrollments}</p>
              </div>
            </div>
            <div className="mt-6">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20 shadow-sm">
                <p className="text-sm text-gray-700 font-medium mb-2">Completion Rate</p>
                <p className="text-4xl font-bold text-primary">{stats.completionRate}%</p>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${stats.completionRate}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  {stats.completedEnrollments} completed out of {stats.totalEnrollments} enrollments
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Users ({users.length})</h3>
            {users.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No users found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Name
                          <SortIcon field="name" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center">
                          Email
                          <SortIcon field="email" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center">
                          Role
                          <SortIcon field="role" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          <SortIcon field="status" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Lesson
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('enrollments')}
                      >
                        <div className="flex items-center">
                          Enrollments
                          <SortIcon field="enrollments" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortUsers(users).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : user.status === 'PENDING_VERIFICATION'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {user.currentLesson ? (
                            user.currentLesson.isCompleted ? (
                              <span className="text-green-600 font-medium">Completed</span>
                            ) : !user.currentLesson.started ? (
                              <span className="text-gray-400">Not started</span>
                            ) : (
                              <div className="flex flex-col">
                                <span className="text-gray-900 font-medium">
                                  Lesson {user.currentLesson.number}
                                </span>
                              </div>
                            )
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.enrollments.length} course{user.enrollments.length !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => navigate(`/users/${user.id}`)}
                            className="text-primary hover:text-primaryDark font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default SchoolDetail;

